import { useCallback } from "react";

/**
 * Generic back-navigation utility for AI screens reached via a
 * "new-navigate-to-ai-program" cross-slice jump (the mediatorId round trip).
 *
 * Many different legacy/terminal-driven screens can jump into the same AI
 * screen (route). Each forward jump carries, inside `parameters`:
 *   - screenParams:        the display data needed by this screen
 *   - mediatorId:          the terminal session id used to resume the caller
 *   - returnProgramCode:   the program code of the caller to navigate back to
 *
 * This hook builds and fires the "navigate-to-program-internal" postMessage
 * needed to return to that caller. Field ordering for the return trip is
 * data-driven via RETURN_PARAM_ORDER below, so adding support for a new jump
 * source is a one-line registry entry instead of bespoke handleBack code.
 *
 * mediatorId is always appended automatically as the final positional value,
 * matching OptumTerminalCommunicationService.startSpecificProcess, which
 * resolves it by position via that program's own inputParameterNames.
 */

// Maps a return program code to the ordered list of paramsData fields sent
// back on the return trip. mediatorId is appended automatically - do not
// list it here.
const RETURN_PARAM_ORDER = {
  RCDSC005: ["dscScheduleName"],
  RCBSC005: ["bscScheduleName"],
  RCOPC005: ["opcScheduleName"],
};

const useAiProgramBackNavigation = ({
  programCode = "",
  mediatorId = "",
  paramsData = {},
}) => {
  const isAiProgramReturn = Boolean(programCode) && Boolean(mediatorId);

  const handleBackToAiProgram = useCallback(() => {
    if (!isAiProgramReturn) return false;

    const fieldOrder = RETURN_PARAM_ORDER[programCode] || [];
    const parameters = [
      ...fieldOrder.map((field) => paramsData?.[field]),
      mediatorId,
    ];

    window.parent.postMessage(
      {
        message: "navigate-to-program-internal",
        programCode,
        parameters,
      },
      "*",
    );
    return true;
  }, [isAiProgramReturn, programCode, mediatorId, paramsData]);

  return { isAiProgramReturn, handleBackToAiProgram };
};

export default useAiProgramBackNavigation;
